import { z } from 'zod';

/**
 * Stable, machine-readable error codes.
 *
 * The backend never returns localized prose. It returns a code plus structured
 * parameters, and the frontend maps the code to a translated message
 * (architecture documentation sections 10.2 and 16). Adding a code is a
 * backwards-compatible change; changing the meaning of one is not.
 */
export const ERROR_CODES = [
  'validation.failed',
  'validation.unknown_field',
  'auth.required',
  'auth.session_expired',
  'auth.invalid_state',
  'auth.token_invalid',
  'auth.account_disabled',
  /**
   * The identity provider vouched for the person, but their Microsoft Entra
   * tenant is not one this deployment trusts (`OIDC_ALLOWED_TENANT_IDS`).
   * Distinct from `auth.account_disabled`: there is no account here to
   * disable, and from `authz.*`: nothing about permissions is in question,
   * their whole organization is simply not a client of this deployment.
   */
  'auth.tenant_not_registered',
  /**
   * First login for this (issuer, subject) pair, but the email it carries
   * already belongs to another account in the organization -- one that is
   * not a claimable `invited` row (already active, disabled, or already
   * bound to a different identity). Provisioning a second account would
   * violate the one-row-per-email constraint; the person needs an
   * administrator to sort out which account is theirs.
   */
  'auth.account_exists',
  'auth.csrf_invalid',
  'authz.permission_denied',
  'authz.resource_forbidden',
  /**
   * A member of an organization other than the host reached a route that has
   * not been opened to them (`@ExternalAccess`). Default-deny, so a route
   * nobody thought about is refused rather than unscoped
   * (docs/organizations-design.md section 3.3).
   */
  'authz.external_forbidden',
  'resource.not_found',
  'resource.conflict',
  'resource.version_mismatch',
  'resource.limit_exceeded',
  /**
   * A delete the database refused because something still points at the row.
   * The backstop for a reference no service checked first: every delete that
   * knows its dependents names them with a code of its own, and this is what
   * the one that does not answers instead of a 500.
   */
  'resource.in_use',
  /**
   * A layer style names a field the registry cannot resolve -- a module that
   * was removed, or a typo in an import. Reported when the style is saved, so
   * the person who wrote it is the person who hears about it.
   */
  'map.style_field_unknown',
  /** The organization's layer styles between them name more distinct fields
   *  than the collection has slots to carry (MAX_STYLE_FIELDS). */
  'map.style_fields_exhausted',
  'view.schema_unsupported',
  'view.component_unknown',
  'view.configuration_invalid',
  'integration.unavailable',
  'integration.rate_limited',
  'request.rate_limited',
  'request.payload_too_large',
  'schedule.circular_dependency',
  'schedule.hierarchy_cycle',
  'schedule.dependency_endpoint_has_children',
  'schedule.owner_required',
  'schedule.approval_required',
  'schedule.session_conflict',
  'schedule.session_not_editable',
  'schedule.session_required',
  'schedule.change_note_required',
  /*
   * Progress (docs/schedule-progress-design.md). The refusal and the
   * explanation are the same string: each of these is what the API throws and,
   * unchanged, the reason the control that would have sent it is disabled.
   */
  'schedule.progress_no_children',
  'schedule.progress_not_reportable',
  'schedule.progress_source_unavailable',
  'schedule.progress_future_report',
  'cost.account_not_posting',
  'cost.account_has_history',
  'cost.account_cycle',
  'cost.account_archived',
  'cost.ledger_type_mismatch',
  'cost.budget_version_immutable',
  'cost.budget_reason_required',
  'cost.budget_not_draft',
  'cost.budget_draft_exists',
  'cost.entry_source_owned',
  'cost.entry_voided',
  'cost.currency_rate_missing',
  'cost.import_invalid',
  /**
   * The record belongs to an external source, which masters it. Distinct from
   * `cost.entry_source_owned`, which is about one already-written movement:
   * this one says the account itself is somebody else's to write, so the write
   * belongs in that system rather than here.
   */
  'cost.source_owned',
  /** An account may not be claimed twice for the same kind of data. */
  'cost.claim_conflict',
  /**
   * The subtree being claimed still holds manually authored entries. Refused
   * rather than absorbed, because the same invoice arriving from both places is
   * how a project comes to be over budget on paper with nothing to point at.
   */
  'cost.claim_manual_entries',
  /** A `synced` source has nothing to pull without an integration attached. */
  'cost.source_integration_required',
  /**
   * The integration named does not exist, is not a cost-capable provider (an
   * ops-only one like `http-endpoint`), or already feeds a different source --
   * `Integration.id` feeds at most one `CostSource` (schema.prisma).
   */
  'cost.source_integration_unsuitable',
  'cost.forecast_version_immutable',
  'cost.forecast_not_draft',
  'cost.forecast_draft_exists',
  /**
   * Declared for the period-close phase, which is designed and not built
   * (docs/cost-editing-design.md, section 12). The policy already takes the
   * fact as an input, so arriving at it later adds a fact rather than a branch
   * in every form.
   */
  'cost.period_closed',
  /**
   * The meeting's minutes are approved, and approved minutes are a record.
   * Distinct from a permission failure: the caller may well hold
   * `meeting.manage`, and would be allowed to edit any meeting that was still
   * open. The correction path is an appended note, not an edit.
   */
  'meeting.not_editable',
  /** The requested state is not reachable from the current one -- see
   *  `canTransitionMeeting`. */
  'meeting.invalid_transition',
  /** Agendas nest one level. A child of a child is refused rather than
   *  flattened, because flattening silently moves somebody's item. */
  'meeting.agenda_depth',
  /** A participant is either an account or a name, never both and never
   *  neither. */
  'meeting.participant_ambiguous',
  /** The same object twice on one agenda item. Refused rather than deduped, so
   *  the second attempt is visibly a no-op rather than silently one. */
  'meeting.reference_duplicate',
  /** A register kind does not offer the requested status -- a decision cannot
   *  be "in progress". See `isStatusAllowedForKind`. */
  'register.status_not_offered',
  /** A vote was cast on a kind whose catalog entry does not offer voting. */
  'discussion.not_offered',
  /** A reply was addressed to a reply. Threads nest one level. */
  'discussion.reply_depth',
  /** Editing or deleting somebody else's comment without `discussion.moderate`. */
  'discussion.not_author',
  /** A field the kind does not use was given a value: a probability on a
   *  decision, a due date on an idea. Refused rather than dropped, since a
   *  value silently discarded is a value the author believes was saved. */
  'register.field_not_offered',
  /**
   * The uploaded bytes, sniffed rather than trusted from the declared
   * `Content-Type`, do not match an allowed type for the declared purpose --
   * including a type that is refused outright, such as SVG
   * (docs/rich-text-design.md section 5.4).
   */
  'asset.invalid_content_type',
  /* ------------------------------------------------------------- fleet */
  /** A declaration would overlap one that already exists for the same vehicle
   *  on the same agreement. Refused rather than merged: two periods that touch
   *  are either one period somebody entered twice or a gap somebody meant to
   *  leave, and the system cannot tell which. */
  'fleet.assignment_overlaps',
  /** The declared period falls outside the agreement's own term. A vehicle
   *  cannot have served a contract before it started. */
  'fleet.assignment_outside_agreement',
  /** A vehicle already withdrawn from service cannot be declared onto a new
   *  agreement. */
  'fleet.vehicle_withdrawn',
  /** An agreement cannot leave `draft` without the award date that decides
   *  which reference period it counts in. */
  'fleet.award_date_required',
  /** Deleting a vehicle that has been declared. The declaration is what an
   *  authority reported; withdraw it first, deliberately. */
  'fleet.vehicle_has_assignments',
  /** Deleting a procurement unit that still has agreements, or one that still
   *  has children. */
  'fleet.unit_in_use',
  /** A procurement unit named itself, or a cycle, as its parent. */
  'fleet.unit_cycle',
  /** A declaration that has already been taken back cannot be edited or
   *  verified. What was claimed is frozen once it is withdrawn. */
  'fleet.assignment_withdrawn',
  /** The administrator role: it holds the whole catalog and keeps the
   *  deployment administrable, so it is neither edited nor deleted. Every other
   *  system role is the client's to adjust or retire. */
  'role.administrator_immutable',
  /** Renaming a system role. Its name is a translation key, rendered in every
   *  locale; its permissions are still editable. */
  'role.system_name_fixed',
  'role.has_members',
  /**
   * A role is still referenced by an email-domain allowlist entry
   * (access-control.ts) and cannot be deleted while that mapping exists --
   * the same "has history" reasoning as `role.has_members`, for a grant that
   * has not happened yet but is still promised.
   */
  'role.referenced_by_allowlist',
  /**
   * A role holding permissions an external member may not hold, offered to
   * one: granted, attached to a domain rule for an external organization, or
   * kept by an account being moved into one -- or a role already held by one
   * being widened past that ceiling.
   */
  'party.role_not_external',
  /** Deleting an organization people still belong to. Move them first. */
  'party.has_members',
  /** The host organization is the client itself; it cannot be deleted. */
  'party.host_immutable',
  /** Deleting an organization an email-domain rule still sends people to. */
  'party.referenced_by_allowlist',
  /** Deleting an organization that still holds contracts. A contract with no
   *  party is not a contract. */
  'party.has_contracts',
  /** Deleting an organization that still operates vehicles in the fleet register. */
  'party.has_vehicles',
  /** Deleting an organization that is still the supplier on clean-vehicle agreements. */
  'party.has_agreements',
  /** Deleting a reference line that positions are still recorded on. Clear or
   *  move them first; a line is never deleted out from under them. */
  'reference_line.in_use',
  /** A lead inviting an address that a domain rule sends to another organization. */
  'party.email_domain_elsewhere',
  /** A lead inviting an address no domain rule sends to an organization that invites only on its own. */
  'party.email_domain_not_theirs',
  /**
   * Revoking the platform role from this account would leave the deployment
   * with none: nobody left who can administer it, or grant it back. Checked
   * transactionally against a live count, not a pre-check, to close the race
   * between two concurrent revocations.
   */
  'platform_admin.last_administrator',
  /**
   * The deployment is not open for tenant use -- still being provisioned, or
   * suspended. Distinct from an authorization failure on purpose: nothing is
   * wrong with the caller or their permissions, the installation itself is
   * closed, and telling them "forbidden" would send them to their
   * administrator instead of to us.
   */
  'deployment.closed',
  'deployment.component_disabled',
  'deployment.module_required',
  'deployment.not_ready',
  'internal.error',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];
export const errorCodeSchema = z.enum(ERROR_CODES);

export const fieldErrorSchema = z.object({
  /** JSON Pointer to the offending member of the request body. */
  pointer: z.string(),
  code: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
});
export type FieldError = z.infer<typeof fieldErrorSchema>;

/**
 * RFC 9457 Problem Details, extended with the machine-readable `code`,
 * interpolation `params`, and the correlation identifier required by
 * architecture documentation section 10.2.
 */
export const problemDetailsSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  code: errorCodeSchema,
  params: z.record(z.string(), z.unknown()).optional(),
  correlationId: z.string(),
  errors: z.array(fieldErrorSchema).optional(),
});
export type ProblemDetails = z.infer<typeof problemDetailsSchema>;

/** Default HTTP status for each code, so call sites cannot drift apart. */
export const ERROR_STATUS: Record<ErrorCode, number> = {
  'validation.failed': 400,
  'validation.unknown_field': 400,
  'auth.required': 401,
  'auth.session_expired': 401,
  'auth.invalid_state': 400,
  'auth.token_invalid': 401,
  'auth.account_disabled': 403,
  'auth.tenant_not_registered': 403,
  'auth.account_exists': 409,
  'auth.csrf_invalid': 403,
  'authz.permission_denied': 403,
  'authz.resource_forbidden': 403,
  'authz.external_forbidden': 403,
  'resource.not_found': 404,
  'resource.conflict': 409,
  'resource.version_mismatch': 412,
  'resource.limit_exceeded': 422,
  'resource.in_use': 409,
  'map.style_field_unknown': 422,
  'map.style_fields_exhausted': 409,
  'view.schema_unsupported': 422,
  'view.component_unknown': 422,
  'view.configuration_invalid': 422,
  'integration.unavailable': 503,
  'integration.rate_limited': 429,
  'request.rate_limited': 429,
  'request.payload_too_large': 413,
  'schedule.circular_dependency': 422,
  'schedule.hierarchy_cycle': 422,
  'schedule.dependency_endpoint_has_children': 422,
  'schedule.owner_required': 422,
  /* The edit is valid; it just is not this person's alone to make. */
  'schedule.approval_required': 409,
  'schedule.session_conflict': 409,
  'schedule.session_not_editable': 409,
  'schedule.session_required': 409,
  'schedule.change_note_required': 422,
  'schedule.progress_no_children': 422,
  'schedule.progress_not_reportable': 403,
  'schedule.progress_source_unavailable': 422,
  'schedule.progress_future_report': 422,
  'cost.account_not_posting': 422,
  'cost.account_has_history': 409,
  'cost.account_cycle': 422,
  'cost.account_archived': 409,
  'cost.ledger_type_mismatch': 422,
  'cost.budget_version_immutable': 409,
  'cost.budget_reason_required': 422,
  'cost.budget_not_draft': 409,
  'cost.budget_draft_exists': 409,
  'cost.entry_source_owned': 409,
  'cost.entry_voided': 409,
  'cost.currency_rate_missing': 422,
  'cost.import_invalid': 422,
  'cost.source_owned': 409,
  'cost.claim_conflict': 409,
  'cost.claim_manual_entries': 409,
  'cost.source_integration_required': 422,
  'cost.source_integration_unsuitable': 422,
  'cost.forecast_version_immutable': 409,
  'cost.forecast_not_draft': 409,
  'cost.forecast_draft_exists': 409,
  'cost.period_closed': 409,
  /* The meeting is closed to edits, not the caller unauthorized. 409 rather
   * than 403, so the client can tell "this is finished" from "this is not
   * yours" and offer the correction path instead of a login prompt. */
  'meeting.not_editable': 409,
  'meeting.invalid_transition': 409,
  'meeting.agenda_depth': 422,
  'meeting.participant_ambiguous': 422,
  'meeting.reference_duplicate': 409,
  'register.status_not_offered': 422,
  'discussion.not_offered': 422,
  'discussion.reply_depth': 422,
  'discussion.not_author': 403,
  'register.field_not_offered': 422,
  'asset.invalid_content_type': 422,
  'fleet.assignment_overlaps': 409,
  'fleet.assignment_outside_agreement': 422,
  'fleet.vehicle_withdrawn': 422,
  'fleet.award_date_required': 422,
  'fleet.vehicle_has_assignments': 409,
  'fleet.unit_in_use': 409,
  'fleet.unit_cycle': 422,
  'fleet.assignment_withdrawn': 409,
  'role.administrator_immutable': 409,
  'role.system_name_fixed': 422,
  'role.has_members': 409,
  'role.referenced_by_allowlist': 409,
  'party.role_not_external': 409,
  'party.has_members': 409,
  'party.host_immutable': 409,
  'party.referenced_by_allowlist': 409,
  'party.has_contracts': 409,
  'party.has_vehicles': 409,
  'party.has_agreements': 409,
  'reference_line.in_use': 409,
  'party.email_domain_elsewhere': 409,
  'party.email_domain_not_theirs': 409,
  'platform_admin.last_administrator': 409,
  /* InfrastructureAsset Unavailable: the installation is closed, not the caller refused. */
  'deployment.closed': 503,
  'deployment.component_disabled': 422,
  'deployment.module_required': 422,
  'deployment.not_ready': 422,
  'internal.error': 500,
};
